/**
 * Groups a transcript into the rows `Messages.tsx` draws, kept in a plain .ts file (no JSX) so it
 * can run under `node --test` directly — Node's type stripping erases annotations but cannot
 * transform JSX, so anything a `.test.ts` imports has to stay JSX-free.
 */

import type { AssistantBlock, AssistantMessage, Message, ToolCallContent, UserMessage } from "../types";
// With the extension: `node --test` loads this module directly and resolves no bare paths.
import { isShown } from "./notices.ts";

export interface UserRow {
	kind: "user";
	index: number;
	message: UserMessage;
}

/**
 * One stretch of a run: the thinking and the work behind a single thing the reader sees.
 *
 * A run is cut at each answer, so thinking that comes after an answer is shown after it rather
 * than in a box above everything. Thinking that merely spans several messages still shares one
 * segment -- the boundary is an answer, never a message -- so a single think/work/answer cycle,
 * by far the common case, is one segment.
 */
export interface RunSegment {
	/** Every thinking block in this stretch, in order, blank-line separated. Empty when it did none. */
	thinking: string;
	/** The calls made in this stretch. Calls a profile draws as a component are not here — see `hasRenderer`. */
	toolCalls: ToolCallContent[];
	/** What the reader sees at the end of it. Empty while the stretch is still running. */
	messages: Message[];
}

/** One agent_start→agent_end cycle: everything after a user message until the next one. */
export interface Run {
	kind: "run";
	/** Where the run starts in the message array. Not an identity, just a stable React key. */
	index: number;
	/** Always at least one. Only the last can still be running. */
	segments: RunSegment[];
	/** Whether the last thing the run produced is thinking. While the run is live, that means it
	    is thinking right now; a tool call, text or tool result after the thinking closes it. */
	thinkingOpen: boolean;
}

export type Row = UserRow | Run;

/** A blank line, so two merged thinking segments read as separate paragraphs rather than one. */
const GAP = "\n\n";

/**
 * @param hasRenderer Whether a profile draws this tool's call as a component of its own. Passed in
 * rather than imported so this module stays JSX-free and therefore testable: `lookupRenderer` lives
 * in a `.tsx` file, which `node --test` cannot load.
 */
export function groupIntoRuns(messages: Message[], hasRenderer: (toolName: string) => boolean = () => false): Row[] {
	const rows: Row[] = [];
	let run: Run | null = null;
	// The stretch being filled, and whether it has already produced something the reader sees.
	// `answered` is what turns the next thinking block or tool call into a new segment rather
	// than more of this one.
	let seg: RunSegment | null = null;
	let answered = false;

	const openSegment = (): RunSegment => {
		const fresh: RunSegment = { thinking: "", toolCalls: [], messages: [] };
		run!.segments.push(fresh);
		seg = fresh;
		answered = false;
		return fresh;
	};

	for (let index = 0; index < messages.length; index++) {
		const message = messages[index]!;
		if (message.role === "user") {
			rows.push({ kind: "user", index, message: message as UserMessage });
			run = null;
			seg = null;
			continue;
		}
		// A message the reader never sees is no reason to open a run: it would leave an empty
		// agent bubble behind. Nor does it end a segment, so it splits no thinking from its answer.
		if (!isShown(message)) continue;
		if (run === null) {
			run = { kind: "run", index, segments: [], thinkingOpen: false };
			rows.push(run);
			openSegment();
		}
		// A tool result is drawn inside the tool call it answers, looked up by id, so it is not a
		// row of its own. A package's status line is drawn by `Notice` in Messages.tsx.
		// It does mean the run is past its thinking, though: the model is now waiting on a tool.
		if (message.role === "toolResult") {
			run.thinkingOpen = false;
			continue;
		}

		if (message.role !== "assistant") {
			seg!.messages.push(message);
			continue;
		}

		// Both boxes are filled from here, and whatever is left is what the reader sees in the stream.
		// Leaving a lifted block in place as well would simply draw it twice.
		const reply = message as AssistantMessage;
		// One slice per segment this message contributes visible blocks to. Almost always one; a
		// message that answers and then thinks again spans two, and its blocks have to land either
		// side of the boundary rather than all in whichever segment happened to be current at the end.
		const slices: Array<{ seg: RunSegment; blocks: AssistantBlock[] }> = [];
		for (const block of reply.content) {
			// The last block wins: a streamed message is replaced whole on every update, so its
			// final block is the one being produced right now.
			run.thinkingOpen = block.type === "thinking";
			// A call the profile renders as a component is output -- a table or a form put in front of
			// the reader to read or answer -- so it stays where the model put it, and counts as an
			// answer. Every other call is the run working, which is what the box is for.
			const isWork = block.type === "thinking" || (block.type === "toolCall" && !hasRenderer(block.name));
			if (isWork && answered) openSegment();

			if (block.type === "thinking") {
				seg!.thinking = seg!.thinking === "" ? block.thinking : seg!.thinking + GAP + block.thinking;
				continue;
			}
			if (isWork) {
				seg!.toolCalls.push(block as ToolCallContent);
				continue;
			}
			const slice = slices.at(-1);
			if (slice?.seg === seg) slice.blocks.push(block);
			else slices.push({ seg: seg!, blocks: [block] });
			answered = true;
		}

		// A message whose every block moved into a box has nothing left to draw. Keeping it would
		// leave an empty row -- and the grid gap around it -- behind each lifted tool call. A message
		// that failed still has its error to show, so it stays.
		if (slices.length === 0) {
			if (reply.errorMessage !== undefined) seg!.messages.push({ ...reply, content: [] });
			continue;
		}
		const { errorMessage: _dropped, ...withoutError } = reply;
		slices.forEach((s, i) => {
			// The error belongs to the message, so it is reported once — on the last slice, where
			// the failure actually left off — rather than repeated under every segment it touched.
			const last = i === slices.length - 1;
			s.seg.messages.push(last ? { ...reply, content: s.blocks } : { ...withoutError, content: s.blocks });
		});
	}

	return rows;
}
